import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isCanceled, useLatestRequest } from "../../lib/useLatest";
import { apiErr } from "../../lib/apiErr";
import AnnouncementForm from "./AnnouncementForm";
import AnnouncementTable from "./AnnouncementTable";
import MaintenancePanel from "./MaintenancePanel";
import { KINDS, STATUS_LABEL } from "./annUtil";
import { PageHeader } from "../../components/PageHeader";

const SEL = "rounded-xl border border-line bg-white px-3 py-2 text-sm";
const PAGE = 25;

function AnnouncementsTab({ readOnly }) {
  const [filter, setFilter] = useState({ status: "", kind: "" });
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const latest = useLatestRequest();
  const load = useCallback(() => {
    const params = { limit: PAGE, offset: page * PAGE, ...(filter.status && { status: filter.status }), ...(filter.kind && { kind: filter.kind }) };
    return api.get("/platform/announcements", { params, signal: latest() }).then((r) => setData(r.data)).catch((e) => { if (isCanceled(e)) return; setData({ items: [], total: 0 }); toast.error(apiErr(e, "Gagal memuat pengumuman")); });
  }, [filter, page, latest]);
  useEffect(() => { load(); }, [load]);
  const setF = (k, v) => { setFilter((f) => ({ ...f, [k]: v })); setPage(0); };
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <select className={SEL} value={filter.status} onChange={(e) => setF("status", e.target.value)} data-testid="ann-filter-status"><option value="">Semua status</option>{Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={SEL} value={filter.kind} onChange={(e) => setF("kind", e.target.value)} data-testid="ann-filter-kind"><option value="">Semua jenis</option>{Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        {!readOnly && !editing && <button onClick={() => setEditing({})} className="btn-grad flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold" data-testid="ann-new"><Plus size={13} /> Pengumuman baru</button>}
      </div>
      {editing && <AnnouncementForm key={editing.id || "new"} doc={editing.id ? editing : null} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {!data ? <Loader2 size={14} className="animate-spin text-slate-400" /> : <AnnouncementTable items={data.items} onEdit={(a) => { setEditing(a); window.scrollTo({ top: 0, behavior: "smooth" }); }} reload={load} readOnly={readOnly} />}
      {data && data.total > PAGE && (
        <div className="flex items-center justify-end gap-2 text-xs text-slate-500" data-testid="ann-pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-line bg-white px-3 py-1.5 disabled:opacity-40">Sebelumnya</button>
          <span>{page + 1} / {pages} · {data.total} total</span>
          <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-line bg-white px-3 py-1.5 disabled:opacity-40">Berikutnya</button>
        </div>
      )}
    </div>
  );
}

export default function PlatformAnnouncements({ readOnly }) {
  const [tab, setTab] = useState("ann");
  return (
    <div data-testid="platform-announcements">
      <PageHeader title="Pengumuman & Pemberitahuan" description="Kirim info fitur baru, update, info umum & jadwal pemeliharaan ke seluruh pengguna — lewat banner aplikasi dan/atau notifikasi (bell in-app + push)." />
      <div className="tabs mt-5">
        {[["ann", "Pengumuman"], ["maint", "Maintenance"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`ann-tab-${k}`} className={`tab ${tab === k ? "tab-active" : ""}`}>{l}</button>)}
      </div>
      <div className="mt-5">{tab === "ann" ? <AnnouncementsTab readOnly={readOnly} /> : <MaintenancePanel readOnly={readOnly} />}</div>
    </div>
  );
}
